import { useState, type FormEvent } from 'react'
import { useNavigate, Navigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@claritio/frontend/lib/auth'
import { landingPath } from '@claritio/frontend/lib/recentOrgs'
import { apiErrorMessage } from '@claritio/frontend/lib/apiErrorMessage'
import { ApiError } from '@claritio/frontend/lib/api'
import { BRAND } from '@claritio/frontend/lib/brand'
import AuthLayout from '@claritio/frontend/components/AuthLayout'
import AuthBrandPanel from '@claritio/frontend/components/AuthBrandPanel'
import AuthLegalLine from '@claritio/frontend/components/AuthLegalLine'
import OAuthButtons from '@claritio/frontend/components/OAuthButtons'
import { Button } from '@claritio/frontend/components/ui/button'
import { Input } from '@claritio/frontend/components/ui/input'
import { Label } from '@claritio/frontend/components/ui/label'

export default function Login() {
  const { t } = useTranslation('admin')
  const { me, loading, login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (!loading && me?.user) return <Navigate to={landingPath()} replace />

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      navigate(landingPath(), { replace: true })
    } catch (err) {
      const staff = err instanceof ApiError && (err.body as { code?: string } | null)?.code === 'staff_requires_google'
      setError(staff ? t('login.staffRequiresGoogle') : apiErrorMessage(err, t('login.error')))
      setSubmitting(false)
    }
  }

  // id="email", id="password" and the single button[type=submit] are a contract
  // with apps/ai-assessment/e2e/helpers/auth.ts, which ~9 smoke specs sign in
  // through. Login.test.tsx guards them; e2e only runs behind the run-e2e label.
  return (
    <AuthLayout
      title={t('login.welcome', { brand: BRAND.name })}
      subtitle={t('login.subtitle')}
      aside={<AuthBrandPanel />}
      footer={<AuthLegalLine />}
    >
      <OAuthButtons returnTo={landingPath()} divider="after" email={email} />
      <form onSubmit={onSubmit}>
        {error && (
          <div className="mb-4 rounded-lg bg-danger-soft px-3 py-2 text-body text-danger">
            {error}
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="email">{t('login.email')}</Label>
          <Input id="email" type="email" autoFocus value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder={t('login.emailPlaceholder')} />
        </div>
        <div className="mt-4 grid gap-2">
          <Label htmlFor="password">{t('login.password')}</Label>
          <Input id="password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} />
        </div>
        <Button type="submit" className="mt-6 w-full" disabled={submitting}>
          {submitting ? t('login.submitting') : t('login.submit')}
        </Button>
      </form>
      <p className="mt-6 text-muted-foreground">
        <Link to="/forgot-password" className="font-medium text-primary hover:underline">{t('login.forgot')}</Link>
      </p>
    </AuthLayout>
  )
}
